import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { Plus, Radio, Trash2 } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import {
	botBuilderKeys,
	createScenarioChat,
	deleteScenarioChat,
	listScenarioChats,
	type ScenarioChat,
} from "@/entities/bot-builder"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { EmptyState, TableSkeleton } from "@/shared/ui/empty-state"
import { Field } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { NativeSelect } from "@/shared/ui/native-select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table"
import { WithDraft, WorkspaceSection } from "@/widgets/bot-workspace"
import { LoadSwap } from "@/shared/ui/load-swap"
import { AutoHeight } from "@/shared/ui/auto-height"

const TYPE_LABELS: Record<ScenarioChat["type"], string> = {
	channel: "Канал",
	supergroup: "Супергруппа",
	group: "Группа",
}

const SUBTITLE =
	"Каналы и чаты, с которыми работает бот: на них проверяется подписка в воронках. Бот должен быть администратором канала."

/** «Конструктор → Группы и каналы». */
export function ChatsPage() {
	return (
		<WithDraft title="Группы и каналы" subtitle={SUBTITLE}>
			{({ draft }) => <ChatsTable scenarioId={draft.id} />}
		</WithDraft>
	)
}

function ChatsTable({ scenarioId }: { scenarioId: string }) {
	const queryClient = useQueryClient()
	const { confirm } = useConfirm()
	const [adding, setAdding] = useState(false)
	const [form, setForm] = useState({
		title: "",
		type: "channel" as ScenarioChat["type"],
		username: "",
		chatId: "",
	})
	const key = botBuilderKeys.chats(scenarioId)
	const chats = useQuery({ queryKey: key, queryFn: () => listScenarioChats(scenarioId) })
	const create = useMutation({
		mutationFn: () =>
			createScenarioChat(scenarioId, {
				title: form.title.trim(),
				type: form.type,
				...(form.username.trim() ? { username: form.username.trim() } : {}),
				...(form.chatId.trim() ? { chatId: form.chatId.trim() } : {}),
			}),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: key })
			setAdding(false)
			setForm({ title: "", type: "channel", username: "", chatId: "" })
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось добавить чат")),
	})

	return (
		<WorkspaceSection
			title="Группы и каналы"
			subtitle={SUBTITLE}
			actions={
				<Button className="rounded-xl" onClick={() => setAdding(true)}>
					<Plus />
					Добавить чат
				</Button>
			}
		>
			<LoadSwap
				loading={chats.isLoading}
				id={chats.isError ? "error" : chats.data?.length ? "list" : "empty"}
			>
				{chats.isLoading ? (
					<TableSkeleton rows={3} />
				) : chats.isError ? (
					<LoadErrorState onRetry={() => void chats.refetch()} />
				) : !chats.data?.length ? (
					<EmptyState
						icon={Radio}
						title="Чатов пока нет"
						action={
							<Button className="rounded-xl" onClick={() => setAdding(true)}>
								<Plus />
								Добавить чат
							</Button>
						}
					>
						Добавьте канал, чтобы воронки могли проверять подписку на него. Бот должен быть его
						администратором.
					</EmptyState>
				) : (
					<AutoHeight className="overflow-x-auto rounded-2xl border border-border bg-card">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-32">Тип чата</TableHead>
									<TableHead>Название</TableHead>
									<TableHead>Username</TableHead>
									<TableHead>ID чата</TableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{chats.data.map((chat) => (
									<TableRow key={chat.id}>
										<TableCell className="text-muted-foreground">
											{TYPE_LABELS[chat.type]}
										</TableCell>
										<TableCell className="font-medium">{chat.title}</TableCell>
										<TableCell>
											{chat.username ? (
												<a
													href={`https://t.me/${chat.username}`}
													target="_blank"
													rel="noreferrer"
													className="text-primary"
												>
													@{chat.username}
												</a>
											) : (
												"–"
											)}
										</TableCell>
										<TableCell className="font-mono text-xs">{chat.chatId ?? "–"}</TableCell>
										<TableCell>
											<Button
												variant="ghost"
												size="icon"
												aria-label={`Убрать «${chat.title}»`}
												onClick={() =>
													confirm({
														title: `Убрать «${chat.title}» из списка?`,
														message: "Проверки подписки в блоках не изменятся.",
														confirmText: "Убрать",
														onConfirm: async () => {
															try {
																await deleteScenarioChat(scenarioId, chat.id)
															} catch (error) {
																toast.error(extractApiErrorMessage(error, "Не удалось убрать чат"))
															}
															await queryClient.invalidateQueries({ queryKey: key })
														},
													})
												}
											>
												<Trash2 />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</AutoHeight>
				)}
			</LoadSwap>

			<Dialog open={adding} onOpenChange={setAdding}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Новый чат</DialogTitle>
						<DialogDescription>
							Публичный канал: по username, приватный: по id вида -1001234567890.
						</DialogDescription>
					</DialogHeader>
					<form
						className="space-y-4"
						onSubmit={(event) => {
							event.preventDefault()
							if (form.title.trim()) create.mutate()
						}}
					>
						<Field label="Название" htmlFor="chat-title">
							<Input
								id="chat-title"
								placeholder="Например, Канал клуба"
								maxLength={128}
								value={form.title}
								onChange={(event) => setForm({ ...form, title: event.target.value })}
							/>
						</Field>
						<Field label="Тип" htmlFor="chat-type">
							<NativeSelect
								id="chat-type"
								value={form.type}
								onChange={(event) =>
									setForm({ ...form, type: event.target.value as ScenarioChat["type"] })
								}
							>
								{Object.entries(TYPE_LABELS).map(([value, label]) => (
									<option key={value} value={value}>
										{label}
									</option>
								))}
							</NativeSelect>
						</Field>
						<Field label="Username" htmlFor="chat-username">
							<Input
								id="chat-username"
								placeholder="@channel или t.me/channel"
								value={form.username}
								onChange={(event) => setForm({ ...form, username: event.target.value })}
							/>
						</Field>
						<Field label="ID чата" htmlFor="chat-id">
							<Input
								id="chat-id"
								inputMode="numeric"
								placeholder="-1001234567890"
								value={form.chatId}
								onChange={(event) => setForm({ ...form, chatId: event.target.value })}
							/>
						</Field>
						<DialogFooter>
							<Button type="submit" disabled={!form.title.trim() || create.isPending}>
								Добавить
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</WorkspaceSection>
	)
}
